import { createBrowserRouter } from "react-router-dom"
import { MainLayout, RouteErrorBoundary } from "@/components/layout"
import { SuspenseWrapper } from "@/components/layout/PageLoader"
import { Navigate } from "react-router-dom"
import { useUserStore } from "@/stores/userStore"
import { useAuthStore } from "@/stores/authStore"
import type { ReactNode } from "react"

// Lazy load pages
import { lazy } from "react"

const HomePage = lazy(() => import("@/features/home/HomePage"))
const HotspotsPage = lazy(() => import("@/features/hotspots/HotspotsPage"))
const EventsPage = lazy(() => import("@/features/events/EventsPage"))
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage"))
const OnboardingPage = lazy(() => import("@/features/onboarding/OnboardingPage"))
const HelpPage = lazy(() => import("@/features/help/HelpPage"))
const PrivacyPage = lazy(() => import("@/features/privacy/PrivacyPage"))
const LoginPage = lazy(() => import("@/features/auth/LoginPage"))
const ResetPasswordPage = lazy(() => import("@/features/auth/ResetPasswordPage"))
const TermsPage = lazy(() => import("@/features/legal/TermsPage"))
const EarningsPage = lazy(() => import("@/features/earnings/EarningsPage"))
const NotFoundPage = lazy(() => import("@/features/notfound/NotFoundPage"))
const ShiftPlanPage = lazy(() => import("@/features/shift/ShiftPlanPage"))
const ChatPage = lazy(() => import("@/features/chat/ChatPage"))
const FuelPage = lazy(() => import("@/features/fuel/FuelPage"))

function OnboardingGate({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const onboardingCompleted = useUserStore((s) => s.onboardingCompleted)

  const completed = Boolean(user?.onboarding_completed ?? onboardingCompleted)
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  if (!completed) {
    return <Navigate to="/onboarding" replace />
  }
  return <>{children}</>
}

function OnboardingRouteGate({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const onboardingCompleted = useUserStore((s) => s.onboardingCompleted)
  const completed = Boolean(user?.onboarding_completed ?? onboardingCompleted)

  if (isAuthenticated && completed) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <RouteErrorBoundary fallbackTitle="Login failed to load">
        <OnboardingRouteGate>
          <SuspenseWrapper>
            <LoginPage />
          </SuspenseWrapper>
        </OnboardingRouteGate>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/onboarding",
    element: (
      <RouteErrorBoundary fallbackTitle="Onboarding failed to load">
        <OnboardingRouteGate>
          <SuspenseWrapper>
            <OnboardingPage />
          </SuspenseWrapper>
        </OnboardingRouteGate>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <RouteErrorBoundary fallbackTitle="Reset password failed to load">
        <SuspenseWrapper>
          <ResetPasswordPage />
        </SuspenseWrapper>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/",
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Home failed to load">
              <SuspenseWrapper>
                <HomePage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "hotspots",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Hotspots failed to load">
              <SuspenseWrapper>
                <HotspotsPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "events",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Events failed to load">
              <SuspenseWrapper>
                <EventsPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "shift",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Shift Planner failed to load">
              <SuspenseWrapper>
                <ShiftPlanPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "chat",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Chat failed to load">
              <SuspenseWrapper>
                <ChatPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "earnings",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Earnings failed to load">
              <SuspenseWrapper>
                <EarningsPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "fuel",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Fuel failed to load">
              <SuspenseWrapper>
                <FuelPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "settings",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Settings failed to load">
              <SuspenseWrapper>
                <SettingsPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "help",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Help failed to load">
              <SuspenseWrapper>
                <HelpPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "privacy",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Privacy failed to load">
              <SuspenseWrapper>
                <PrivacyPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "terms",
        element: (
          <OnboardingGate>
            <RouteErrorBoundary fallbackTitle="Terms failed to load">
              <SuspenseWrapper>
                <TermsPage />
              </SuspenseWrapper>
            </RouteErrorBoundary>
          </OnboardingGate>
        ),
      },
      {
        path: "*",
        element: (
          <SuspenseWrapper>
            <NotFoundPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
])
